// ============================================================================
// FNE-BRIDGE Frontend - Hook d'authentification
// ============================================================================

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { authService } from '../services';
import { PUBLIC_ROUTES } from '../config/routes';
import type { LoginRequest, ValidateOtpRequest, ChangePasswordRequest } from '../types/api';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user, company, tokens, isAuthenticated, isLoading, error,
    otpSessionId, otpMaskedEmail, requiresOtp, mustChangePassword,
    setLoading, setError, setOtpSession, clearOtpSession, setAuth, logout: storeLogout,
  } = useAuthStore();

  const setLicenseExpired = useAuthStore((s) => s.setLicenseExpired);

  const login = useCallback(async (data: LoginRequest) => {
    setLoading(true);
    try {
      const response = await authService.login(data);
      const result = response.data.data;
      if (result.requires_otp) {
        setOtpSession(result.otp_session_id, result.masked_email);
      } else if ((result as any).tokens) {
        // Mode dev : OTP désactivé, tokens retournés directement
        const { user, company, tokens, license_expired } = result as any;
        setAuth(user, company, tokens);
        setLicenseExpired(!!license_expired);
        if (license_expired) {
          navigate('/settings');
        } else {
          navigate('/');
        }
      }
      return result;
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || 'Erreur de connexion';
      setError(message);
      throw err;
    }
  }, [setLoading, setError, setOtpSession, setAuth, setLicenseExpired, navigate]);

  const validateOtp = useCallback(async (data: ValidateOtpRequest) => {
    setLoading(true);
    try {
      const response = await authService.validateOtp(data);
      const { user, company, tokens, license_expired } = response.data.data;
      setAuth(user, company, tokens);
      setLicenseExpired(!!license_expired);
      clearOtpSession();
      if (license_expired) {
        navigate('/settings');
      } else {
        navigate('/');
      }
      return response.data.data;
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || 'Code OTP invalide';
      setError(message);
      throw err;
    }
  }, [setLoading, setError, setAuth, setLicenseExpired, clearOtpSession, navigate]);

  const changePassword = useCallback(async (data: ChangePasswordRequest) => {
    setLoading(true);
    try {
      await authService.changePassword(data);
      setLoading(false);
    } catch (err: any) {
      const message = err.response?.data?.error?.message || err.response?.data?.message || 'Erreur lors du changement de mot de passe';
      setError(message);
      throw err;
    }
  }, [setLoading, setError]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignorer les erreurs de logout
    } finally {
      storeLogout();
      navigate(PUBLIC_ROUTES.LOGIN);
    }
  }, [storeLogout, navigate]);

  return {
    user, company, tokens, isAuthenticated, isLoading, error,
    otpSessionId, otpMaskedEmail, requiresOtp, mustChangePassword,
    login, validateOtp, changePassword, logout,
    clearError: () => setError(null),
  };
}
